import React, { useState, useEffect, useRef } from "react";
import { Modal, View, Text, StyleSheet, TouchableOpacity, Image, ScrollView, Platform, ActivityIndicator } from "react-native";
import { DataTable, IconButton } from "react-native-paper";
import { WebView } from "react-native-webview";
import * as XLSX from "xlsx";
import * as docx from "docx-preview";

// Conditional import for native PDF to prevent Web build errors
const Pdf = Platform.OS !== 'web' ? require('react-native-pdf').default : null;

// Conditional import for FortuneSheet (Web only)
let FortuneWorkbook: any = null;
if (Platform.OS === 'web') {
  try {
    FortuneWorkbook = require('@fortune-sheet/react').Workbook;
    require('@fortune-sheet/react/dist/index.css');
  } catch (e) {
    console.warn("FortuneSheet failed to load:", e);
  }
}

type FileViewerProps = {
  visible: boolean;
  onClose: () => void;
  fileName: string;
  blob: Blob | null;
  loading: boolean;
};

export default function AttachmentViewerModal({ visible, onClose, fileName, blob, loading }: FileViewerProps) {
  const [fileType, setFileType] = useState<string>("");
  const [blobUrl, setBlobUrl] = useState<string>("");
  const [excelData, setExcelData] = useState<any[][]>([]);
  const [fortuneData, setFortuneData] = useState<any[]>([]);
  const [textContent, setTextContent] = useState<string>("");
  const [docxReady, setDocxReady] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const docxContainerRef = useRef<any>(null);

  useEffect(() => {
    if (visible && blob) {
      const parts = fileName.split(".");
      const ext = parts.length > 1 ? parts.pop()?.toLowerCase() || "" : "";
      setFileType(ext);

      let finalBlob: Blob | File = blob;
      // Use File object on Web to provide a filename hint and ensure correct MIME type
      if (Platform.OS === "web") {
        const mimeType = ext === "pdf" ? "application/pdf" : blob.type || "application/octet-stream";
        finalBlob = new File([blob], fileName, { type: mimeType });
      }
      
      const url = URL.createObjectURL(finalBlob);
      setBlobUrl(url);
      
      if (ext === "xlsx" || ext === "xls" || ext === "csv") {
        parseExcel(finalBlob);
      } else if (["txt", "json", "log", "xml", "md"].includes(ext)) {
        parseText(finalBlob);
      } else if (ext === "docx" && Platform.OS === "web") {
        setDocxReady(true);
      }
    } else {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl("");
      }
      setExcelData([]);
      setFortuneData([]);
      setTextContent("");
      setDocxReady(false);
      setError("");
    }
  }, [visible, blob]);

  useEffect(() => {
    if (visible && docxReady && blob && docxContainerRef.current && Platform.OS === 'web') {
      docx.renderAsync(blob, docxContainerRef.current)
        .catch(err => {
          console.error("Error rendering docx:", err);
          setError("Failed to render Word document preview.");
        });
    }
  }, [visible, docxReady, blob]);

  const parseText = async (fileBlob: Blob) => {
    try {
      const text = await fileBlob.text();
      setTextContent(text);
    } catch (err) {
      console.error("Error parsing text:", err);
      setError("Failed to read text file.");
    }
  };

  const parseExcel = async (fileBlob: Blob) => {
    try {
      const arrayBuffer = await fileBlob.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      
      if (Platform.OS === 'web' && FortuneWorkbook) {
        const sheets = workbook.SheetNames.map((name, index) => {
          const sheet = workbook.Sheets[name];
          const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" }) as any[][];
          const celldata: any[] = [];
          
          json.forEach((row, r) => {
            row.forEach((cell, c) => {
              if (cell !== "" && cell !== null && cell !== undefined) {
                celldata.push({
                  r,
                  c,
                  v: { 
                    v: cell, 
                    m: String(cell),
                    ct: { fa: "General", t: "g" }
                  }
                });
              }
            });
          });

          const range = XLSX.utils.decode_range(sheet['!ref'] || "A1");
          return {
            name,
            celldata,
            row: Math.max(range.e.r + 1, 30),
            column: Math.max(range.e.c + 1, 15),
            status: index === 0 ? 1 : 0,
            order: index
          };
        });
        setFortuneData(sheets);
      } else {
        // Fallback or Native implementation
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
        setExcelData(json);
      }
    } catch (err) {
      console.error("Error parsing excel:", err);
      setError("Failed to parse Excel file.");
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <View style={styles.center}>
          <ActivityIndicator size="large" color="#007BFF" />
          <Text style={styles.loadingText}>Loading file...</Text>
        </View>
      );
    }

    if (!blob && !loading) {
      return (
        <View style={styles.center}>
          <Text>No file data available.</Text>
        </View>
      );
    }

    if (error) {
      return (
        <View style={styles.center}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      );
    }

    // PDF Rendering
    if (fileType === "pdf") {
      if (Platform.OS === "web") {
        return (
          <View style={{ flex: 1 }}>
            <iframe
              key={blobUrl}
              src={blobUrl}
              style={{ width: "100%", height: "100%", border: "none", minHeight: 600 }}
              title={fileName}
            />
            <View style={styles.pdfFooter}>
              <TouchableOpacity 
                style={styles.openButton}
                onPress={() => window.open(blobUrl, '_blank')}
              >
                <Text style={styles.openButtonText}>Open PDF in New Tab</Text>
              </TouchableOpacity>
              <Text style={styles.hintText}>If the PDF doesn't appear above, use the button to open it.</Text>
            </View>
          </View>
        );
      } else {
        return Pdf ? (
          <Pdf
            source={{ uri: blobUrl }}
            style={{ flex: 1, width: '100%', height: '100%' }}
            trustAllCerts={false}
            onError={(error: any) => {
              console.error("PDF Error:", error);
              setError("Failed to render PDF on this device.");
            }}
          />
        ) : (
          <View style={styles.center}>
            <Text>PDF viewer not available on this platform.</Text>
          </View>
        );
      }
    }

    // Image Rendering
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "jfif"].includes(fileType)) {
      return (
        <View style={styles.imageContainer}>
          <Image source={{ uri: blobUrl }} style={styles.image} resizeMode="contain" />
        </View>
      );
    }

    // Excel/CSV Rendering
    if (["xlsx", "xls", "csv"].includes(fileType)) {
      if (Platform.OS === 'web' && FortuneWorkbook && fortuneData.length > 0) {
        return (
          <View style={{ flex: 1, backgroundColor: '#fff' }}>
            <FortuneWorkbook 
              data={fortuneData} 
              onChange={setFortuneData}
              readOnly={true}
              showToolbar={false}
              showFormulaBar={false}
              showSheetTabs={true}
              columnHeaderHeight={25}
              rowHeaderWidth={40}
            />
          </View>
        );
      }

      return (
        <ScrollView horizontal>
          <ScrollView>
            <DataTable>
              {excelData.map((row, rowIndex) => (
                <DataTable.Row key={`row-${rowIndex}`}>
                  {row.map((cell, cellIndex) => (
                    <DataTable.Cell key={`cell-${rowIndex}-${cellIndex}`} style={styles.tableCell}>
                      <Text>{cell?.toString() || ""}</Text>
                    </DataTable.Cell>
                  ))}
                </DataTable.Row>
              ))}
            </DataTable>
          </ScrollView>
        </ScrollView>
      );
    }

    // Text Rendering
    if (["txt", "json", "log", "xml", "md"].includes(fileType)) {
      return (
        <ScrollView style={styles.textContainer}>
          <Text style={styles.textContent}>{textContent}</Text>
        </ScrollView>
      );
    }

    // Word Rendering
    if (fileType === "docx") {
      if (Platform.OS === "web") {
        return (
          <ScrollView style={styles.docxContainer}>
             <div ref={docxContainerRef} />
          </ScrollView>
        );
      } else {
        return (
          <View style={styles.center}>
            <Text>Word preview is optimized for Web.</Text>
          </View>
        );
      }
    }

    // Fallback for PPTX and others
    return (
      <View style={styles.center}>
        <Text style={styles.unsupportedText}>
          Preview not available for .{fileType} files.
        </Text>
        <TouchableOpacity 
          style={styles.downloadButton}
          onPress={() => {
            const link = document.createElement("a");
            link.href = blobUrl;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
        >
          <Text style={styles.downloadButtonText}>Download instead</Text>
        </TouchableOpacity>
      </View>
    );
  };

  const handleDownload = () => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
              <Text style={styles.title} numberOfLines={1}>{fileName}</Text>
            </View>
            <View style={{ flexDirection: 'row' }}>
              <IconButton 
                icon="download" 
                size={24} 
                onPress={handleDownload} 
                iconColor="#007BFF"
              />
              <IconButton icon="close" size={24} onPress={onClose} />
            </View>
          </View>
          <View style={styles.content}>
            {renderContent()}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.8)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalContainer: {
    width: "100%",
    height: "90%",
    backgroundColor: "white",
    borderRadius: 12,
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    backgroundColor: "#f8f9fa",
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333",
    flex: 1,
    marginRight: 10,
  },
  content: {
    flex: 1,
    backgroundColor: "#fff",
  },
  center: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  imageContainer: {
    flex: 1,
    backgroundColor: "#f0f0f0",
  },
  image: {
    width: "100%",
    height: "100%",
  },
  tableCell: {
    minWidth: 120,
    borderRightWidth: 1,
    borderRightColor: "#eee",
  },
  loadingText: {
    marginTop: 10,
    color: "#666",
  },
  errorText: {
    color: "#d9534f",
    textAlign: "center",
  },
  unsupportedText: {
    fontSize: 16,
    color: "#666",
    marginBottom: 20,
    textAlign: "center",
  },
  downloadButton: {
    backgroundColor: "#007BFF",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
  },
  downloadButtonText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 16,
  },
  pdfFooter: {
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderTopWidth: 1,
    borderTopColor: "#eee",
    alignItems: "center",
  },
  openButton: {
    backgroundColor: "#28a745",
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    marginBottom: 5,
  },
  openButtonText: {
    color: "#fff",
    fontWeight: "bold",
  },
  hintText: {
    fontSize: 12,
    color: "#666",
  },
  textContainer: {
    flex: 1,
    padding: 15,
    backgroundColor: "#fdfdfd",
  },
  textContent: {
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
    fontSize: 14,
    color: "#333",
  },
  docxContainer: {
    flex: 1,
    padding: 20,
    backgroundColor: "#fff",
  },
});
